import React, { useState, useEffect, useRef } from 'react';

/**
 * Searchable customer dropdown with avatar initials.
 *
 * Before: embedded as a 100-line sub-component inside InvoiceModal.js.
 * Now a standalone, reusable component.
 */

const getInitials = (name) => {
  if (!name) return '';
  const words = name.split(' ');
  return words.length >= 2
    ? (words[0][0] + words[1][0]).toUpperCase()
    : name.substring(0, 2).toUpperCase();
};

const getColor = (str) => {
  const colors = ['#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b'];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export default function SearchableCustomerSelect({ value, customers, onSelect, onCreateCustomer }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (value) {
      const customer = customers.find((c) => c.id.toString() === value.toString());
      setDisplayValue(customer ? customer.full_name : '');
      setSearchTerm('');
    } else {
      setSearchTerm('');
      setDisplayValue('');
    }
  }, [value, customers]);

  const activeFilter = searchTerm.trim().toLowerCase();
  const filtered = activeFilter
    ? customers.filter(
        (c) =>
          c.full_name?.toLowerCase().includes(activeFilter) ||
          c.email?.toLowerCase().includes(activeFilter)
      )
    : customers;

  const handleSelect = (customer) => {
    setDisplayValue(customer.full_name || '');
    setSearchTerm('');
    setIsOpen(false);
    onSelect?.(customer);
  };

  const handleFocus = () => {
    setIsOpen(true);
    setSearchTerm('');
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const inputValue = searchTerm !== '' ? searchTerm : displayValue;

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => { setSearchTerm(e.target.value); setIsOpen(true); }}
          onFocus={handleFocus}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          className="w-full pl-3 pr-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-base pointer-events-none">
        </span>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-2 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-gray-500 text-xs p-1"
        >
          ▼
        </button>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 w-[400px] mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-72 overflow-y-auto z-[10000]">
          {typeof onCreateCustomer === 'function' && (
            <div
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setIsOpen(false);
                setSearchTerm('');
                onCreateCustomer?.();
              }}
              className="flex items-center gap-2 p-3 cursor-pointer border-b border-gray-100 hover:bg-gray-50 text-sm text-indigo-600"
            >
              <span className="text-base">+</span>
              <span className="font-medium">Create customer</span>
            </div>
          )}
          {filtered.length > 0 ? (
            filtered.map((customer) => (
              <div
                key={customer.id}
                onClick={() => handleSelect(customer)}
                className="flex items-center gap-3 p-3 cursor-pointer border-b border-gray-50 hover:bg-gray-50"
              >
                <div
                  className="w-8 h-8 rounded text-white flex items-center justify-center text-xs shrink-0"
                  style={{ backgroundColor: getColor(customer.full_name || '') }}
                >
                  {getInitials(customer.full_name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900">{customer.full_name}</div>
                  {customer.email && (
                    <div className="text-xs text-gray-500 mt-0.5">{customer.email}</div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="p-3 text-gray-500 text-sm">No customers found</div>
          )}
        </div>
      )}
    </div>
  );
}
